/* Feb-2026 · Cost Preflight Dialog.
 *
 * Shown before any AI generation the user hasn't already approved.
 * The user sees:
 *   • Which provider handle is doing the work (Ask OPi / Claude / GEMi)
 *   • Estimated words of output
 *   • Estimated USD cost at the current pricing tier (30% markup +
 *     Stripe folded in via lib/cost.estimatePriceUsd)
 *   • Whether it fits inside their remaining monthly budget
 *   • A checkbox to skip this dialog for future jobs under $X
 *
 * Callers pass `words`, `model`, `remainingUsd`, plus onConfirm/onCancel.
 * When the checkbox is ticked, the user's threshold is stored in
 * localStorage — no backend hop — and re-applied to future jobs
 * whose estimate falls under that threshold.
 */
import React, { useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sparkles, DollarSign, AlertTriangle, ShieldCheck } from 'lucide-react';
import { estimatePriceUsd, estimateCogsUsd, fmtUsd, DETECTION_SCORES } from '@/lib/cost';
import { providerHandle, providerTagline } from '@/lib/providerLabel';

const SKIP_KEY = 'cost_preflight_skip_under_usd_v1';

export function getPreflightSkipThreshold() {
  try {
    const v = parseFloat(localStorage.getItem(SKIP_KEY) || '');
    return Number.isFinite(v) ? v : 0;
  } catch { return 0; }
}

export function setPreflightSkipThreshold(usd) {
  try { localStorage.setItem(SKIP_KEY, String(usd)); } catch { /* ignore */ }
}

/** Should we skip the dialog for this estimated cost? */
export function shouldSkipPreflight(estimatedUsd) {
  const threshold = getPreflightSkipThreshold();
  return threshold > 0 && estimatedUsd <= threshold;
}

export default function CostPreflightDialog({
  open, onOpenChange,
  words, model, action = 'generate this',
  remainingUsd, onConfirm, onCancel,
}) {
  const [skipFuture, setSkipFuture] = useState(false);

  const {
    priceUsd, cogsUsd, over, handle, tagline, detection,
  } = useMemo(() => {
    const p = estimatePriceUsd(words, model).final_price_usd;
    const c = estimateCogsUsd(words, model).total;
    return {
      priceUsd: p,
      cogsUsd:  c,
      over:     remainingUsd != null && p > remainingUsd,
      handle:   providerHandle(model),
      tagline:  providerTagline(model),
      detection: DETECTION_SCORES[model?.id],
    };
  }, [words, model, remainingUsd]);

  const confirm = () => {
    if (skipFuture) setPreflightSkipThreshold(Math.max(priceUsd * 1.5, 1));
    onConfirm?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="cost-preflight-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Sparkles className="h-4 w-4 text-primary" />
            About to Ask {handle} to {action}
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            {tagline} · we always price the work before it runs, so your running
            cost is never a surprise.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-1 text-[12px]">
          <Row label="Estimated output">
            <span className="font-mono-stat tabular-nums">
              ~{(words || 0).toLocaleString()} words
            </span>
          </Row>
          <Row label="Model">
            <span>
              {model?.label || 'Default'}
              {detection != null && (
                <Badge variant="outline" className="ml-1.5 text-[9.5px]">
                  Detection evasion {detection}/100
                </Badge>
              )}
            </span>
          </Row>
          <Row label="Est. your cost" strong>
            <span className="inline-flex items-center gap-1 rounded-sm bg-primary/10 px-2 py-0.5 font-mono-stat text-primary tabular-nums">
              <DollarSign className="h-3 w-3" /> {fmtUsd(priceUsd)}
            </span>
          </Row>
          <Row label="Raw AI cost">
            <span className="font-mono-stat text-muted-foreground tabular-nums">
              {fmtUsd(cogsUsd)}
            </span>
          </Row>
          {remainingUsd != null && (
            <Row label="Remaining this month">
              <span className={over
                ? 'font-mono-stat font-semibold tabular-nums text-red-700 dark:text-red-300'
                : 'font-mono-stat tabular-nums'}>
                {fmtUsd(remainingUsd)}
              </span>
            </Row>
          )}
        </div>

        {over && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-red-300 bg-red-50 p-2 text-[11px] text-red-900 dark:bg-red-950/30 dark:text-red-200"
               data-testid="cost-preflight-over-budget">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5" />
            This job would push you past your monthly cap. Raise the cap in
            Settings or top up word credits before running.
          </div>
        )}

        <label className="mt-2 flex items-center gap-2 text-[10.5px] text-muted-foreground">
          <input type="checkbox"
                 checked={skipFuture}
                 onChange={(e) => setSkipFuture(e.target.checked)}
                 data-testid="cost-preflight-skip-future"
                 className="h-3 w-3" />
          Don&apos;t ask again for jobs under {fmtUsd(Math.max(priceUsd * 1.5, 1))}
        </label>

        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm"
                  onClick={() => { onCancel?.(); onOpenChange?.(false); }}
                  data-testid="cost-preflight-cancel">
            Cancel
          </Button>
          <Button size="sm"
                  disabled={over}
                  onClick={() => { confirm(); onOpenChange?.(false); }}
                  data-testid="cost-preflight-approve">
            <ShieldCheck className="mr-1 h-3.5 w-3.5" /> Approve &amp; Run
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children, strong }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className={strong ? 'text-[11.5px] font-semibold' : 'text-[11.5px] text-muted-foreground'}>
        {label}
      </span>
      {children}
    </div>
  );
}
